import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { useGetMatchChannels } from "../../../../queries/matchScheduler";
import { useGetScenes } from "../../../../queries/scenes";
import { useEntitlements } from "../../../../hooks/useEntitlements";
import { useGetCampaigns } from "../../../../queries/campaigns";

/**
 * Builds the Gen 2 create payload.
 *
 * Exported as a pure function so the contract can be tested without rendering
 * PrimeReact — @testing-library/react is not installed in this repo, and the
 * thing worth testing is the payload shape, not the widgets.
 *
 * The API-Football normalised fixture is almost 1:1 with the backend contract;
 * only the logo keys differ (home_logo -> home_team_logo). Optional keys are
 * omitted entirely rather than sent as null, so the backend's `nullable` rules
 * never see an explicit null for a field the user simply left alone.
 */
export function buildMatchSchedulePayload(fixture, cfg = {}) {
    if (!fixture) return null;

    const {
        durationMin = 105, sceneId, postSceneId, channelId,
        halftimeCampaignId, defaultCampaignId, notes = "",
    } = cfg;

    return {
        fixture_id:     String(fixture.fixture_id),
        league_id:      fixture.league_id,
        league_name:    fixture.league_name,
        home_team:      fixture.home_team,
        away_team:      fixture.away_team,
        home_team_logo: fixture.home_logo ?? null,
        away_team_logo: fixture.away_logo ?? null,
        kickoff_at:     fixture.kickoff_at,
        match_duration_min: durationMin,
        ...(sceneId            && { scene_id: sceneId }),
        ...(postSceneId        && { post_match_scene_id: postSceneId }),
        ...(channelId          && { channel_id: channelId }),
        ...(halftimeCampaignId && { halftime_campaign_id: halftimeCampaignId }),
        ...(defaultCampaignId  && { default_campaign_id: defaultCampaignId }),
        ...(notes.trim()       && { notes: notes.trim() }),
    };
}

/**
 * Unwrap a react-query result to an ARRAY of rows, whatever shape it arrives in.
 *
 * Exported for test. The previous one-liner was
 *   q.data?.data ?? q.data ?? []
 * which looks total but is not: `?? q.data` hands back ANY truthy value, array
 * or not. useGetCampaigns is a useInfiniteQuery, whose data is
 * `{pages, pageParams}` with no `.data` key — so both campaign Dropdowns were
 * handed a plain object, PrimeReact called .map() on it (dropdown.esm.js:248)
 * and the whole form white-screened. It only surfaced once fixture discovery
 * started returning rows, because until then nobody could reach step 2.
 *
 * The final `return []` is the point: an unrecognised shape now renders an
 * empty picker with its emptyMessage instead of taking the page down.
 */
export const listOf = (q) => {
    const d = q?.data;
    if (!d) return [];
    if (Array.isArray(d)) return d;                 // bare array
    if (Array.isArray(d.data)) return d.data;       // CompanyResource / PaginationResources
    if (Array.isArray(d.pages)) {                   // useInfiniteQuery
        return d.pages.flatMap((page) => page?.items ?? page?.data ?? []);
    }
    return [];
};

/**
 * The schedule form body. Chrome (Dialog, footer, step control) lives in
 * MatchSchedulerDialog; this renders fields and reports config upward.
 *
 * Every orchestration target is optional — the backend accepts a schedule with
 * none of them and the phase orchestrator degrades gracefully ("skipped — no
 * kickoff scene_id"), so a venue with no hardware can still schedule a match.
 *
 * Pickers whose source is gated off (CheckFeature answers 402, not 403) render
 * EMPTY with an actionable emptyMessage rather than disappearing — a missing
 * control is harder to diagnose than an empty one that says why.
 */
export default function MatchSchedulerForm({ fixture, value, onChange }) {
    const { t } = useTranslation();

    const [cfg, setCfg] = useState(value ?? { durationMin: 105, notes: "" });
    const set = (k, v) => {
        const next = { ...cfg, [k]: v };
        setCfg(next);
        onChange?.(next);
    };

    useEffect(() => { onChange?.(cfg); /* eslint-disable-next-line */ }, []);

    // retry:false so a gated source settles on its 402 immediately instead of
    // retrying three times while the user stares at a spinner. All four hooks
    // take (params, options) — scenes/campaigns ignored the second argument
    // entirely until 2026-08-20, so this comment used to be false.
    // /scenes is a hardware_control route: not asked when the package explicitly lacks it.
    const hwOn = useEntitlements().has('hardware_control');
    const scenesQ    = useGetScenes({}, { retry: false, enabled: hwOn });
    const channelsQ  = useGetMatchChannels({ retry: false });
    const campaignsQ = useGetCampaigns({ per_page: 50 }, { retry: false });

    const scenes    = listOf(scenesQ);
    const channels  = listOf(channelsQ);
    const campaigns = listOf(campaignsQ);

    const channelLabel = (c) => (c.channel_number ? `${c.name} (${c.channel_number})` : c.name);

    const kickoff = fixture ? new Date(fixture.kickoff_at) : null;
    const end = kickoff ? new Date(kickoff.getTime() + (cfg.durationMin ?? 105) * 60 * 1000) : null;

    return (
        <div className="flex flex-col gap-4">
            {/* Fixture summary */}
            <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <div className="font-semibold text-gray-800">
                    {fixture?.home_team} <span className="text-gray-400 font-normal">vs</span> {fixture?.away_team}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                    {fixture?.league_name}
                    {kickoff && ` · ${kickoff.toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}`}
                    {end && ` → ${end.toLocaleTimeString([], { timeStyle: "short" })}`}
                </div>
            </div>

            <div>
                <label className="block text-xs text-gray-500 mb-1">{t('match_scheduler.duration_min')}</label>
                <InputNumber
                    value={cfg.durationMin ?? 105}
                    onValueChange={(e) => set("durationMin", e.value ?? 105)}
                    min={1} max={300} showButtons className="w-full"
                />
            </div>

            {/* Kickoff / post-match scenes are hardware_control features: hidden entirely (no text, no read) when the
                package explicitly lacks it. cfg is untouched, so any scene ids already in `value` round-trip. */}
            {hwOn && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('match_scheduler.kickoff_scene')}</label>
                    <Dropdown
                        value={cfg.sceneId ?? null} onChange={(e) => set("sceneId", e.value)}
                        options={scenes} optionLabel="name" optionValue="id"
                        placeholder={t('match_scheduler.optional')} showClear className="w-full"
                        emptyMessage={t('match_scheduler.no_scenes')}
                    />
                </div>
                <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('match_scheduler.post_match_scene')}</label>
                    <Dropdown
                        value={cfg.postSceneId ?? null} onChange={(e) => set("postSceneId", e.value)}
                        options={scenes} optionLabel="name" optionValue="id"
                        placeholder={t('match_scheduler.optional')} showClear className="w-full"
                        emptyMessage={t('match_scheduler.no_scenes')}
                    />
                </div>
            </div>
            )}

            {/* ⚠️ The "Screens" MultiSelect that used to live here was REMOVED,
                not disabled — it wrote to match_schedules.screens, and nothing
                in MatchSchedulerService::resolvePlayers() has ever done
                anything with that column (checked: all three call sites loop
                over it and do nothing — "Future: when VenueScreen gets a
                connected_player_id column, resolve here"). It was the
                field a café would naturally reach for to pick a screen, and
                it silently did nothing. Channel below is the ONLY field that
                actually routes a match to a device — see its helper text. */}
            <div>
                <label className="block text-xs text-gray-500 mb-1">{t('match_scheduler.channel')}</label>
                <Dropdown
                    value={cfg.channelId ?? null} onChange={(e) => set("channelId", e.value)}
                    options={channels} optionLabel={channelLabel} optionValue="id"
                    placeholder={t('match_scheduler.optional')} showClear filter className="w-full"
                    emptyMessage={t('match_scheduler.no_channels')}
                />
                <p className="text-xs text-gray-500 mt-1">{t('match_scheduler.channel_helper')}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('match_scheduler.halftime_campaign')}</label>
                    <Dropdown
                        value={cfg.halftimeCampaignId ?? null} onChange={(e) => set("halftimeCampaignId", e.value)}
                        options={campaigns} optionLabel="name" optionValue="id"
                        placeholder={t('match_scheduler.optional')} showClear filter className="w-full"
                        emptyMessage={t('match_scheduler.no_campaigns')}
                    />
                </div>
                <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('match_scheduler.default_campaign')}</label>
                    <Dropdown
                        value={cfg.defaultCampaignId ?? null} onChange={(e) => set("defaultCampaignId", e.value)}
                        options={campaigns} optionLabel="name" optionValue="id"
                        placeholder={t('match_scheduler.optional')} showClear filter className="w-full"
                        emptyMessage={t('match_scheduler.no_campaigns')}
                    />
                </div>
            </div>

            <div>
                <label className="block text-xs text-gray-500 mb-1">{t('match_scheduler.notes')}</label>
                <InputText
                    value={cfg.notes ?? ""} onChange={(e) => set("notes", e.target.value)}
                    maxLength={500} className="w-full"
                />
            </div>
        </div>
    );
}
